import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { router } from "./router";
import "./index.css";
import "@/lib/studio-inspector";

const elem = document.getElementById("root");
if (!elem) throw new Error("Root element #root not found in index.html");
const app = (
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
);

if (import.meta.hot) {
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else {
  createRoot(elem).render(app);
}

// PWA head tags (injected via JS to avoid Bun bundler resolution)
function addLink(rel: string, href: string, attrs?: Record<string, string>) {
  const link = document.createElement("link");
  link.rel = rel;
  link.href = href;
  if (attrs) for (const [k, v] of Object.entries(attrs)) link.setAttribute(k, v);
  document.head.appendChild(link);
}
addLink("manifest", "/manifest.json");
addLink("apple-touch-icon", "/icons/helm-apple-touch-icon.png");
addLink("icon", "/icons/helm-favicon.svg", { type: "image/svg+xml", sizes: "any" });
addLink("icon", "/icons/helm-favicon.png", { type: "image/png", sizes: "64x64" });

// Service Worker Registration — production only. In dev the SW's cached app
// shell goes stale on every code change and its update prompt hijacks
// navigation; HMR covers freshness there. process.env.NODE_ENV is inlined by
// the bundler.
if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        // Check for updates periodically (every 60 minutes)
        setInterval(() => reg.update(), 60 * 60 * 1000);

        reg.addEventListener("updatefound", () => {
          const newWorker = reg.installing;
          if (!newWorker) return;

          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "activated" && navigator.serviceWorker.controller) {
              // New version fetched in the background; it takes over on the
              // next natural navigation. Never block with a modal prompt.
              console.info("App update installed; active on next reload.");
            }
          });
        });
      })
      .catch((err) => console.error("SW registration failed:", err));
  });
} else if ("serviceWorker" in navigator) {
  // Dev: proactively drop any SW + caches left by earlier builds/ports so a
  // stale shell can never hijack this origin's navigation.
  navigator.serviceWorker.getRegistrations().then((regs) => {
    for (const reg of regs) void reg.unregister();
  });
  if ("caches" in window) {
    caches.keys().then((keys) => {
      for (const key of keys) void caches.delete(key);
    });
  }
}
